/**
 * 合集详情页（只读视图）
 *
 * - 任何登录用户都可以访问 public 合集
 * - 私有合集仅 owner 可见
 * - 点击条目跳转到播放器（Phase 1：播放器不感知合集，只是普通播放）
 * - 顶栏与播放页一致：返回 + 标题 +「上传者」副标题；正文区为封面与列表
 * - 「编辑合集」仅当从「我的 -> 我的合集」点入时显示（路由 state）；首页进入即使用户是 owner 也不显示
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { clearAuth, getAuthHeaders, getUser } from '../utils/auth';
import { resolveAssetUrl } from '../utils/assetUrl';
import { DEFAULT_COVER_PATH, handleCoverError } from '../utils/videoCover';
import type { ApiResponse } from '../types/api';
import type { CollectionDetail as CollectionDetailDTO } from '../types/collection';
import AppShell from './AppShell';
import './CollectionDetail.css';
import './VideoPlayerWithSubtitles.css';

const getLanguageName = (langCode: string): string => {
  const map: Record<string, string> = {
    zh: '中文',
    en: '英语',
    ja: '日语',
    ko: '韩语',
    es: '西班牙语',
    fr: '法语',
    de: '德语',
    ru: '俄语',
  };
  return map[langCode] || langCode;
};

const formatDuration = (seconds?: number | null): string => {
  if (!seconds || seconds <= 0) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const mm = m.toString().padStart(2, '0');
  const ss = s.toString().padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
};

const CollectionDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { collectionId } = useParams<{ collectionId: string }>();
  const [data, setData] = useState<CollectionDetailDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentUser = getUser();

  const locationState = location.state as
    | { returnTo?: string; allowCollectionDetailManage?: boolean }
    | null
    | undefined;
  const returnTo = locationState?.returnTo;
  const allowCollectionDetailManage = !!locationState?.allowCollectionDetailManage;
  const handleBack = useCallback(() => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate('/?tab=collections');
    }
  }, [navigate, returnTo]);

  const load = useCallback(async () => {
    if (!collectionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/collections/${collectionId}`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      if (res.status === 404) {
        setError('合集不存在或已被删除');
        return;
      }
      if (res.status === 403) {
        setError('无权查看此合集');
        return;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const payload = (await res.json()) as ApiResponse<CollectionDetailDTO>;
      if (payload.success && payload.data) {
        setData(payload.data);
      } else {
        setError(payload.error || '加载合集失败');
      }
    } catch (err) {
      console.error('[CollectionDetail] 加载失败:', err);
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [collectionId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePlay = (videoId: string) => {
    if (!collectionId) return;
    const params = new URLSearchParams();
    params.set('video', videoId);
    params.set('from', 'collection');
    params.set('collectionId', collectionId);
    if (returnTo) params.set('returnTo', returnTo);
    if (allowCollectionDetailManage) params.set('collectionManage', '1');
    navigate(`/player?${params.toString()}`);
  };

  const renderHeader = (title: string, uploader?: string | null) => (
    <div className="video-header app-shell-header-content">
      <button
        type="button"
        className="header-back-button"
        onClick={(e) => {
          e.stopPropagation();
          handleBack();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleBack();
        }}
        title="返回"
        aria-label="返回"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </button>
      <div className="header-content">
        <div className="header-title">{title}</div>
        {uploader ? <div className="header-uploader">上传者：{uploader}</div> : null}
      </div>
    </div>
  );

  if (loading) {
    return (
      <AppShell header={renderHeader('加载中…', null)} footer={null}>
        <div className="collection-detail-page collection-detail-page--shell">
          <div className="collection-detail-loading">加载中...</div>
        </div>
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell header={renderHeader('合集', null)} footer={null}>
        <div className="collection-detail-page collection-detail-page--shell">
          <div className="collection-detail-error">
            <p>{error}</p>
          </div>
        </div>
      </AppShell>
    );
  }

  if (!data) return null;

  const isOwner = !!currentUser?.id && currentUser.id === data.userId;
  const showOwnerManageEntry = isOwner && allowCollectionDetailManage;
  const heroCover = data.coverUrl
    ? resolveAssetUrl(data.coverUrl) || DEFAULT_COVER_PATH
    : data.items[0]?.videoCoverUrl
      ? resolveAssetUrl(data.items[0].videoCoverUrl) || DEFAULT_COVER_PATH
      : DEFAULT_COVER_PATH;

  const header = renderHeader(data.name, data.username || null);

  return (
    <AppShell header={header} footer={null}>
      <div className="collection-detail-page collection-detail-page--shell">
        <div className="collection-detail-hero">
          <div className="collection-detail-hero__cover">
            <img src={heroCover} alt={data.name} onError={handleCoverError} />
            <div className="thumbnail-placeholder" style={{ display: 'none' }}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            </div>
          </div>
          <div className="collection-detail-hero__meta">
            <div className="collection-detail-hero__badges">
              <span
                className={`collection-detail-badge ${data.visibility === 'public' ? 'is-public' : 'is-private'}`}
              >
                {data.visibility === 'public' ? '公开' : '私有'}
              </span>
              <span className="collection-detail-badge">{getLanguageName(data.sourceLanguage)}</span>
              <span className="collection-detail-badge">{data.items.length} 集</span>
            </div>
            {data.description ? (
              <p className="collection-detail-hero__desc">{data.description}</p>
            ) : null}
            <div className="collection-detail-hero__actions">
              {data.items.length > 0 && (
                <button
                  type="button"
                  className="collection-detail-play-btn"
                  onClick={() => {
                    const first = data.items[0];
                    if (first) handlePlay(first.videoId);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  <span>从头播放</span>
                </button>
              )}
              {showOwnerManageEntry && (
                <button
                  type="button"
                  className="collection-detail-edit-btn"
                  onClick={() => navigate(`/collections/${data.id}/edit`)}
                >
                  编辑合集
                </button>
              )}
            </div>
          </div>
        </div>

        <ul className="collection-detail-items">
          {data.items.map((it) => (
            <li
              key={it.itemId}
              className="collection-detail-item"
              onClick={() => handlePlay(it.videoId)}
            >
              <div className="collection-detail-item__pos">{it.position}</div>
              <div className="collection-detail-item__thumb">
                <img
                  src={
                    it.videoCoverUrl
                      ? resolveAssetUrl(it.videoCoverUrl) || DEFAULT_COVER_PATH
                      : DEFAULT_COVER_PATH
                  }
                  alt={it.videoName || it.videoId}
                  onError={handleCoverError}
                />
                {it.videoDurationSeconds ? (
                  <div className="collection-detail-item__duration">
                    {formatDuration(it.videoDurationSeconds)}
                  </div>
                ) : null}
              </div>
              <div className="collection-detail-item__meta">
                <div className="collection-detail-item__name">{it.videoName || it.videoId}</div>
                <div className="collection-detail-item__sub">
                  {it.videoUsername && <span>@{it.videoUsername}</span>}
                  {it.videoSourceLanguage && (
                    <span>· {getLanguageName(it.videoSourceLanguage)}</span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>

        {data.items.length === 0 && (
          <div className="collection-detail-empty">
            <p>这个合集还没有视频。</p>
            {showOwnerManageEntry && (
              <button
                type="button"
                className="collection-detail-edit-btn"
                onClick={() => navigate(`/collections/${data.id}/edit`)}
              >
                添加视频
              </button>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default CollectionDetail;
